import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { appendSasToken, removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { usePageDocumentDetails } from "./GeoLocationPromotion.hooks";

export const AddEditGeoLocationPromotion = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = usePageDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    const activeTab = query.get("activeTab");
    if (activeTab === "pageHeading") {
      refetchCategories();
    }
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("activeTab"), query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      header: editPageContentById
        ? decodeHTML(editPageContentById.payload?.header)
        : editPageContent
          ? decodeHTML(editPageContent?.header)
          : "",

      header_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.header_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.header_malay)
          : "",

      header_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.header_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.header_mandarin)
          : "",

      short_description: editPageContentById
        ? decodeHTML(editPageContentById.payload?.short_description)
        : editPageContent
          ? decodeHTML(editPageContent?.short_description)
          : "",

      short_description_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.short_description_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.short_description_malay)
          : "",

      short_description_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.short_description_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.short_description_mandarin)
          : "",

      logo_text: editPageContentById
        ? decodeHTML(editPageContentById.payload?.logo_text)
        : editPageContent
          ? decodeHTML(editPageContent?.logo_text)
          : "",

      banner_image: editPageContentById
        ? editPageContentById.payload?.banner_image
          ? [
              {
                uid: "-1",
                name: "bannerImage.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.banner_image),
              },
            ]
          : []
        : editPageContent?.banner_image
          ? [
              {
                uid: "-1",
                name: "bannerImage.jpg",
                status: "done",
                url: appendSasToken(editPageContent.banner_image),
              },
            ]
          : [],

      square_image: editPageContentById
        ? editPageContentById.payload?.square_image
          ? [
              {
                uid: "-1",
                name: "squareImage.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.square_image),
              },
            ]
          : []
        : editPageContent?.square_image
          ? [
              {
                uid: "-1",
                name: "squareImage.jpg",
                status: "done",
                url: appendSasToken(editPageContent.square_image),
              },
            ]
          : [],

      logo: editPageContentById
        ? editPageContentById.payload?.logo
          ? [
              {
                uid: "-1",
                name: "logo.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.logo),
              },
            ]
          : []
        : editPageContent?.logo
          ? [
              {
                uid: "-1",
                name: "logo.jpg",
                status: "done",
                url: appendSasToken(editPageContent.logo),
              },
            ]
          : [],

      type: editPageContentById
        ? decodeHTML(editPageContentById.payload.type)
        : editPageContent
          ? decodeHTML(editPageContent?.type)
          : null,
      shop_category: editPageContentById
        ? (editPageContentById.payload?.shop_category ?? null)
        : (editPageContent?.shop_category ?? null),

      parent_id_promo: editPageContentById
        ? decodeHTML(editPageContentById.payload.parent_id_promo)
        : editPageContent
          ? decodeHTML(editPageContent?.parent_id_promo)
          : null,

      // project_airport_id: editPageContentById
      //   ? decodeHTML(editPageContentById.payload.project_airport_id)
      //   : editPageContent
      //     ? decodeHTML(editPageContent?.project_airport_id)
      //     : "",
      zone_selection: editPageContentById
        ? decodeHTML(editPageContentById.payload.zone_selection)
        : editPageContent
          ? decodeHTML(editPageContent?.zone_selection)
          : null,

      levels: editPageContentById
        ? decodeHTML(editPageContentById.payload.levels)
        : editPageContent
          ? decodeHTML(editPageContent?.levels)
          : null,

      published: editPageContentById
        ? decodeHTML(editPageContentById.payload?.published)
        : editPageContent
          ? decodeHTML(editPageContent?.published)
          : "",
    },

    fields: fields.geoLocationPromotions,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image", "banner_image", "square_image", "logo"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CENTRALIZED");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          const cleanUrl = field[0].url.split("?sp=")[0];
          data[key] = cleanUrl;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    let descriptionKeys: any = ["short_description", "short_description_malay", "short_description_mandarin"];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }

    const payload = {
      action,
      method,
      module: "Centralized Promotions",
      subModule: "geoLocationPromotions",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes,
      appList: [{ appName: "CENTRALIZED", apiUrl }],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
        }),
        ...data,
      },
      status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Geo Location Promotions`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${centralizedHost}/api/geo-location-promotions/${editPageContent?.id || editPageContentById?.payload?.id}` : `${centralizedHost}/api/geo-location-promotions`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this MyAirports Promotions"
      />
    </ContentWrapper>
  );
};
